// src/features/treinos/utils/exerciseStats.js
//
// Agregação PURA das sessões WORKOUT_LOG por exercício (testável, sem Firebase).
// Cada sessão: { DATA, FINALIZADO_AT, EXERCICIOS:[{ EXERCISE_ID, NOME, ALVO,
// SERIES:[{ CARGA, REPS, RPE }] }] }. Match por EXERCISE_ID; fallback por NOME
// normalizado. // TODO: IA referenciar EXERCISE_ID da lib (hoje pode faltar).

import { epley1RM } from "./epley.js";
import { parseReps } from "./loadRecommendation.js";

// nº mínimo de aparições p/ exibir Constância e Melhor sequência (combinado).
export const MIN_APARICOES_STREAK = 3;

export function normNome(s = "") {
  return String(s).normalize("NFD").replace(/\p{Diacritic}/gu, "").trim().toLowerCase();
}

/** Casa um exercício de sessão pelo ID; fallback pelo NOME normalizado. */
export function exMatches(ex, { exerciseId, nomeNorm }) {
  if (exerciseId && ex?.EXERCISE_ID && String(ex.EXERCISE_ID) === String(exerciseId)) return true;
  const nome = ex?.NOME ?? ex?.nome;
  if (nomeNorm && nome && normNome(nome) === nomeNorm) return true;
  return false;
}

function toMillis(v) {
  if (v == null) return 0;
  if (typeof v === "number") return v;
  if (typeof v?.toDate === "function") return v.toDate().getTime();
  const secs = v?._seconds ?? v?.seconds;
  if (typeof secs === "number") return secs * 1000;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function sessionSortKey(s) {
  // DATA "yyyy-mm-dd" ordena lexicograficamente; usamos epoch como desempate.
  return toMillis(s?.FINALIZADO_AT || s?.INICIADO_AT || s?.CREATED_AT) || Date.parse(s?.DATA || "") || 0;
}

/**
 * Agrega tudo que as 6 stats + os 3 cards + o histórico de carga precisam.
 * @param {Array} sessions  docs WORKOUT_LOG
 * @param {{ exerciseId?:string, nome?:string }} ref
 */
export function aggregateExerciseStats(sessions = [], { exerciseId, nome } = {}) {
  const nomeNorm = normNome(nome);
  const rows = [];
  for (const s of sessions || []) {
    const ex = (s?.EXERCICIOS || []).find((e) => exMatches(e, { exerciseId, nomeNorm }));
    if (!ex) continue;
    const series = Array.isArray(ex.SERIES) ? ex.SERIES : [];
    rows.push({
      sortKey: sessionSortKey(s),
      data: s?.DATA || "",
      ex,
      series,
      valid: series.filter((x) => Number(x.CARGA) > 0 && Number(x.REPS) > 0),
      done: series.length > 0,
    });
  }
  rows.sort((a, b) => a.sortKey - b.sortKey);

  let repsTotais = 0;
  let cargaTotal = 0;
  let prCarga = 0;
  let e1rmMax = 0;
  let aparicoes = 0;
  let sessoes = 0;
  let bestStreak = 0;
  let curStreak = 0;
  const points = [];
  let lastDone = null;

  for (const r of rows) {
    aparicoes += 1;
    if (r.done) {
      sessoes += 1;
      curStreak += 1;
      if (curStreak > bestStreak) bestStreak = curStreak;
    } else {
      curStreak = 0;
    }

    for (const x of r.series) repsTotais += Number(x.REPS) || 0;

    let sessMaxCarga = 0;
    let sessMaxE1rm = 0;
    let topSerie = null;
    for (const x of r.valid) {
      const carga = Number(x.CARGA);
      const reps = Number(x.REPS);
      cargaTotal += carga * reps;
      const e = epley1RM(carga, reps);
      if (carga > prCarga) prCarga = carga;
      if (e > e1rmMax) e1rmMax = e;
      if (carga > sessMaxCarga) { sessMaxCarga = carga; topSerie = x; }
      if (e > sessMaxE1rm) sessMaxE1rm = e;
    }
    if (r.valid.length > 0) {
      points.push({ data: r.data, melhorCarga: sessMaxCarga, melhor1RM: sessMaxE1rm });
      lastDone = { r, topSerie };
    }
  }

  let lastTopSet = null;
  if (lastDone) {
    const planReps = parseReps(lastDone.r.ex?.ALVO?.reps);
    const repsDone = Number(lastDone.topSerie?.REPS) || 0;
    lastTopSet = {
      carga: Number(lastDone.topSerie?.CARGA) || 0,
      reps: repsDone,
      rpe: lastDone.topSerie?.RPE == null ? null : Number(lastDone.topSerie.RPE),
      completou: planReps?.value != null ? repsDone >= planReps.value : true,
    };
  }

  return {
    aparicoes,
    sessoes,
    repsTotais,
    cargaTotal,
    prCarga,
    ultimaCarga: lastTopSet?.carga ?? 0,
    e1rmMax,
    bestStreak,
    // Constância só faz sentido com aparições suficientes (decisão de produto).
    constancia: aparicoes >= MIN_APARICOES_STREAK ? sessoes / aparicoes : null,
    mostrarStreak: aparicoes >= MIN_APARICOES_STREAK,
    points,
    lastTopSet,
    // Entrada pronta p/ recomendarCarga (null se não há carga registrada).
    historicoExercicio:
      lastTopSet && lastTopSet.carga > 0
        ? {
            ultimaCarga: lastTopSet.carga,
            completou: lastTopSet.completou,
            rpe: lastTopSet.rpe,
            e1rmMax,
          }
        : null,
  };
}
